import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WidgetProps } from '@rjsf/utils';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { inputClasses } from '../tailwindClassnames';
import { colors } from '../../../theme/colors';

import {
  Box,
  BoxField as Field,
  BoxInputs,
  INVALID_BOX_MESSAGE,
  boxFromBounds,
  isValidBox,
  parseBox,
  parseInputs,
  serializeBox,
  serializeInputs,
} from './boundingBox';

export {
  boxFromBounds,
  isValidBox,
  parseBox,
  serializeBox,
} from './boundingBox';

// Display order: latitudes first (north above south), then longitudes (east, west).
const FIELDS = [
  { key: 'north', label: 'Max Latitude (N)' },
  { key: 'south', label: 'Min Latitude (S)' },
  { key: 'east', label: 'Max Longitude (E)' },
  { key: 'west', label: 'Min Longitude (W)' },
] as const;

// Reads the four inputs by name (not position), or null if any is empty or not a number.
const boxFromInputs = (inputs: BoxInputs): Box | null =>
  parseBox(serializeInputs(inputs));

const BOX_COLOR = colors['teal-t100'];

const cornerIcon = L.divIcon({
  className: '',
  html: '<div style="width:12px;height:12px;background:var(--c-white);border:2px solid var(--c-teal-t100);box-sizing:border-box"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

const BoundingBoxWidget: React.FC<WidgetProps> = ({
  value,
  onChange,
  disabled,
  readonly,
  label,
  schema,
  uiSchema,
}) => {
  // fullWidth fields get no label from the field template, so the widget draws
  // its own header. `label` is the ui:title node from generateUISchema.
  const description =
    (uiSchema?.['ui:description'] as React.ReactNode) ?? schema?.description;
  const { t } = useTranslation();
  const editable = !disabled && !readonly;
  // Only a valid box goes on the map; an invalid one is typed text the user is
  // still fixing, shown in the inputs.
  const parsed = parseBox(value);
  const box = parsed && isValidBox(parsed) ? parsed : null;

  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const rectRef = useRef<L.Rectangle | null>(null);
  const cornersRef = useRef<L.Marker[]>([]);
  const drawingRef = useRef(false);
  const [drawMode, setDrawMode] = useState(false);
  const [inputs, setInputs] = useState<BoxInputs>(parseInputs(value));
  const [cursor, setCursor] = useState<L.LatLng | null>(null);

  // Handlers registered once on the map read the latest props through this ref.
  const latest = useRef({ onChange, editable, drawMode });
  latest.current = { onChange, editable, drawMode };

  // A rejected box (zero area, off the map) must put the drawn rectangle back
  // to the saved value, so the map never disagrees with the inputs.
  const [resync, setResync] = useState(0);
  const commit = useCallback((b: Box) => {
    if (isValidBox(b)) latest.current.onChange(serializeBox(b));
    else setResync((n) => n + 1);
  }, []);

  // Keep the text inputs in step with the stored value (map edits, Clear, reload).
  // Not for the value the user just typed: that would swap their text for its
  // normalized number mid-edit ("-1." becoming "-1", losing the decimal point).
  const keyOf = (v: unknown) =>
    typeof v === 'string' ? v : JSON.stringify(v ?? '');
  const valueKey = keyOf(value);
  const typedKey = useRef<string | null>(null);
  useEffect(() => {
    if (valueKey === typedKey.current) return;
    typedKey.current = null;
    setInputs(parseInputs(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  // Map setup (once).
  useEffect(() => {
    if (!mapEl.current) return;
    const map = L.map(mapEl.current, {
      center: [0, 20],
      zoom: 2,
      worldCopyJump: true,
    });
    mapRef.current = map;
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      { attribution: 'Tiles © Esri', maxZoom: 16 },
    ).addTo(map);
    map.on('mousemove', (e: L.LeafletMouseEvent) => setCursor(e.latlng));
    map.on('mouseout', () => setCursor(null));

    // Draw mode: press-drag-release creates the rectangle. The release is also
    // listened for on the document, so letting go outside the map still ends it.
    let anchor: L.LatLng | null = null;
    let last: L.LatLng | null = null;
    const finish = (end: L.LatLng | null) => {
      document.removeEventListener('mouseup', onDocumentMouseUp);
      if (!anchor || !drawingRef.current) return;
      const b = L.latLngBounds(anchor, end ?? anchor);
      anchor = null;
      last = null;
      drawingRef.current = false;
      setDrawMode(false);
      commit(boxFromBounds(b));
    };
    const onDocumentMouseUp = () => finish(last);
    map.on('mousedown', (e: L.LeafletMouseEvent) => {
      if (!latest.current.drawMode || !latest.current.editable) return;
      anchor = e.latlng;
      last = e.latlng;
      drawingRef.current = true;
      document.addEventListener('mouseup', onDocumentMouseUp);
    });
    map.on('mousemove', (e: L.LeafletMouseEvent) => {
      if (!anchor || !drawingRef.current) return;
      last = e.latlng;
      const b = L.latLngBounds(anchor, e.latlng);
      if (rectRef.current) rectRef.current.setBounds(b);
      else
        rectRef.current = L.rectangle(b, { color: BOX_COLOR, weight: 2 }).addTo(
          map,
        );
    });
    map.on('mouseup', (e: L.LeafletMouseEvent) => finish(e.latlng));

    return () => {
      document.removeEventListener('mouseup', onDocumentMouseUp);
      map.remove();
      mapRef.current = null;
      rectRef.current = null;
      cornersRef.current = [];
    };
  }, [commit]);

  // Drawing needs the map to stop panning.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (drawMode) {
      map.dragging.disable();
      map.getContainer().style.cursor = 'crosshair';
    } else {
      map.dragging.enable();
      map.getContainer().style.cursor = '';
    }
  }, [drawMode]);

  // Render the stored box (rectangle + draggable corners).
  const boxKey = box ? serializeBox(box) : '';
  const fitted = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || drawingRef.current) return;
    cornersRef.current.forEach((m) => m.remove());
    cornersRef.current = [];
    if (!box) {
      rectRef.current?.remove();
      rectRef.current = null;
      return;
    }
    const bounds = L.latLngBounds([box.south, box.west], [box.north, box.east]);
    if (rectRef.current) rectRef.current.setBounds(bounds);
    else
      rectRef.current = L.rectangle(bounds, {
        color: BOX_COLOR,
        weight: 2,
      }).addTo(map);
    if (!fitted.current) {
      map.fitBounds(bounds, { padding: [30, 30] });
      fitted.current = true;
    }
    if (!editable) return;
    // Each corner is the opposite of the one being dragged.
    const corners: { lat: number; lng: number; fixed: [number, number] }[] = [
      { lat: box.north, lng: box.west, fixed: [box.south, box.east] },
      { lat: box.north, lng: box.east, fixed: [box.south, box.west] },
      { lat: box.south, lng: box.east, fixed: [box.north, box.west] },
      { lat: box.south, lng: box.west, fixed: [box.north, box.east] },
    ];
    cornersRef.current = corners.map((c) => {
      const marker = L.marker([c.lat, c.lng], {
        icon: cornerIcon,
        draggable: true,
      }).addTo(map);
      marker.on('drag', () => {
        const p = marker.getLatLng();
        rectRef.current?.setBounds(L.latLngBounds(p, c.fixed));
      });
      marker.on('dragend', () => {
        const b = L.latLngBounds(marker.getLatLng(), c.fixed);
        commit(boxFromBounds(b));
      });
      return marker;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxKey, editable, resync]);

  const useMapView = () => {
    const b = mapRef.current?.getBounds();
    if (b) commit(boxFromBounds(b));
  };

  const clear = () => {
    setDrawMode(false);
    onChange(undefined);
  };

  const onInput = (key: Field, text: string) => {
    const next = { ...inputs, [key]: text };
    setInputs(next);
    const emit = (v: string | undefined) => {
      typedKey.current = keyOf(v);
      onChange(v);
    };
    const b = boxFromInputs(next);
    if (b && isValidBox(b)) {
      emit(serializeBox(b));
      mapRef.current?.fitBounds(
        [
          [b.south, b.west],
          [b.north, b.east],
        ],
        { padding: [30, 30] },
      );
    } else if (FIELDS.every((f) => next[f.key].trim() === '')) {
      emit(undefined);
    } else {
      // Kept exactly as typed, so a form re-render (Save adding its errors) shows
      // the user's text again rather than the last valid box, and so the save
      // validators see it and refuse to save.
      emit(serializeInputs(next));
    }
  };

  const inputsInvalid =
    FIELDS.some((f) => inputs[f.key].trim() !== '') &&
    (() => {
      const b = boxFromInputs(inputs);
      return !b || !isValidBox(b);
    })();

  const btn =
    'flex items-center gap-2 h-9 px-3 rounded border text-sm font-semibold transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed';
  const btnPrimary = `${btn} border-brand-accent-green text-brand-accent-green bg-white enabled:hover:bg-ui-cta-secondary-blue`;

  return (
    <div
      className="flex w-full flex-col gap-3"
      data-testid="BoundingBoxWidget-container"
    >
      {(label || description) && (
        <div className="flex flex-col gap-1">
          {label && <div className="text-ui-text-primary-text">{label}</div>}
          {description && (
            <span className="text-sm text-ui-text-primary-text">
              {description}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-row flex-wrap gap-2">
        <button
          type="button"
          data-testid="BoundingBoxWidget-draw"
          disabled={!editable}
          aria-pressed={drawMode}
          onClick={() => setDrawMode((d) => !d)}
          className={`${btnPrimary} ${drawMode ? 'bg-ui-cta-secondary-blue' : ''}`}
        >
          {t('Draw Box')}
        </button>
        <button
          type="button"
          data-testid="BoundingBoxWidget-mapview"
          disabled={!editable}
          onClick={useMapView}
          className={btnPrimary}
        >
          {t('Use Map View')}
        </button>
        <button
          type="button"
          data-testid="BoundingBoxWidget-clear"
          disabled={!editable || !value}
          onClick={clear}
          className={`${btn} border-unlisted-neutral-gray-200 bg-white text-ui-text-primary-text`}
        >
          {t('Clear')}
        </button>
      </div>

      <div className="relative">
        <div
          ref={mapEl}
          data-testid="BoundingBoxWidget-map"
          className="z-0 h-[360px] w-full rounded border border-unlisted-neutral-gray-200"
        />
        {cursor && (
          <div className="pointer-events-none absolute bottom-6 left-2 z-[500] rounded bg-white px-2 py-1 font-mono text-xs shadow">
            {cursor.lat.toFixed(6)}°, {cursor.lng.toFixed(6)}°
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {FIELDS.map((f) => (
          <label
            key={f.key}
            className="flex flex-col gap-1 text-sm font-medium text-ui-text-primary-text"
          >
            {t(f.label)}
            <input
              type="text"
              inputMode="decimal"
              disabled={!editable}
              data-testid={`BoundingBoxWidget-${f.key}`}
              value={inputs[f.key]}
              onChange={(e) => onInput(f.key, e.target.value)}
              className={`${inputsInvalid ? inputClasses.error : inputClasses.standard} font-normal`}
            />
          </label>
        ))}
      </div>
      {inputsInvalid && (
        <p
          className="text-sm text-ui-status-error-destructive"
          data-testid="BoundingBoxWidget-error"
        >
          {t(INVALID_BOX_MESSAGE as any)}
        </p>
      )}
    </div>
  );
};

export default BoundingBoxWidget;
